import { OnInit, Component, OnDestroy, Input, EventEmitter, Output, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { COMMON_TEXT, MENU_NAME, ROUTE_PATH, OfficeType } from '@core/constants';
import { AuthService } from '@core/services/apis';
import { Menu } from '@core/models/models';
import { FullscreenService, SessionContextService } from '@core/services/common';
import { filter, Subscription } from 'rxjs';
import { Utils } from '@core/helper';

@Component({
  selector: 'caremoni-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
})
export class LayoutComponent implements OnInit, OnDestroy {
  @Input() isLoggedIn: boolean = true;
  @Input() sidebarExpanded: boolean = false;
  @Output() toggleSidebar = new EventEmitter();

  @ViewChild('fullScreenElement') fullScreenElement!: ElementRef;
  
  sidebarData: Menu[] = [];
  title = '';
  menuIcon: string = 'check-sheet';
  isFullScreen = false;

  COMMON_TEXT = COMMON_TEXT;
  corpPrefix: string = '';
  routerSubscriber: Subscription | undefined;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private fullScreenService: FullscreenService,
    private sessionContextService: SessionContextService,
  )
  {
    
  }

  ngOnDestroy(): void {
    this.fullScreenService.removeListener();
    if (this.routerSubscriber) {
      this.routerSubscriber.unsubscribe();
    }
  }

  ngOnInit(): void {
    let officeSelected = this.sessionContextService.getOfficeSelected<any>();
    let officeType = officeSelected.officeType  || OfficeType.newTsusho;
    let isPremiumUser = officeSelected?.isPremiumUser;

    const checkSheetBaseRoute = Utils.isNyusho(officeType)
      ? ROUTE_PATH.CHECK_SHEET_NYUSHO
      : ROUTE_PATH.CHECK_SHEET_TSUSHO;

    const sidebarMenu = [
      {
        id: "1",
        name: MENU_NAME.HOME,
        title: COMMON_TEXT.CHECK_SHEET_TITLE,
        routerLink: checkSheetBaseRoute,
        icon: "check-sheet",
        active: true,
      },
      {
        id: "1-short-time",
        name: 'チェックシート',
        subName: '(短時間型)',
        title: COMMON_TEXT.CHECK_SHEET_SHORT_TIME_TITLE,
        routerLink: ROUTE_PATH.CHECK_SHEET_SHORT_TIME,
        icon: "check-sheet-short-time",
      },
      {
        id: "1-functional-training",
        name: 'チェックシート',
        subName: '(機能訓練排泄)',
        title: COMMON_TEXT.CHECK_SHEET_FUNCTIONAL_TRAINING_TITLE,
        routerLink: ROUTE_PATH.CHECK_SHEET_FUNCTIONAL_TRAINING,
        icon: "check-sheet-functional-training",
      },
      {
        id: "2",
        name: MENU_NAME.EXCREATION,
        title: COMMON_TEXT.EXCREATION,
        routerLink: ROUTE_PATH.EXCRETION_MONITOR,
        icon: "excreation"
      },
      {
        id: "3",
        name: MENU_NAME.BATH_SCREEN,
        title: COMMON_TEXT.BATHING_TITLE,
        routerLink: ROUTE_PATH.BATHING_SCHEDULE,
        icon: "bather"
      },
      {
        id: "4",
        name: MENU_NAME.BATHE_MANAGEMENT,
        title: COMMON_TEXT.BATHE_MANAGEMENT,
        routerLink: ROUTE_PATH.BATHE_MANAGEMENT,
        icon: "bathe-management"
      },
      {
        id: "5",
        name: MENU_NAME.TITLE_SETTINGS,
        title: MENU_NAME.TITLE_SETTINGS,
        routerLink: ROUTE_PATH.TITLE_SETTINGS,
        icon: "title-settings"
      },
      {
        id: "999999",
        name: MENU_NAME.USER_DASHBOARD,
        title: COMMON_TEXT.USER_DASHBOARD,
        routerLink: ROUTE_PATH.USER_DASHBOARD,
        icon: "user-dashboard",
        display: false,
      }
    ];
    this.title = COMMON_TEXT.CHECK_SHEET_TITLE;
    if (isPremiumUser) {
      // Premium
      if (Utils.isNyusho(officeType)) {
        // Nyusho: チェックシート + 機能訓練排泄対応版（入所向け）、短時間型は非表示
        this.sidebarData = sidebarMenu.filter(m => m.icon !== 'check-sheet-short-time');
      } else if (Utils.isTsusho(officeType)) {
        // Tsusho: チェックシート + 短時間型対応版、機能訓練・排泄・user-dashboardは非表示
        this.sidebarData = sidebarMenu.filter(m => m.icon !== 'check-sheet-functional-training' && m.icon !== 'user-dashboard' && m.icon !== 'excreation');
      }
    } else {
      // Not Premium
      if (Utils.isNyusho(officeType)) {
        this.sidebarData = sidebarMenu.filter(m => m.icon !== 'check-sheet-short-time');
      } else {
        this.sidebarData = sidebarMenu.filter(m => m.icon !== 'check-sheet-functional-training' && m.icon !== 'user-dashboard' && m.icon !== 'excreation');
      }
    }
    this.fullScreenService.fullscreenState$.subscribe(state => {
      this.isFullScreen = state.isFullscreen;
    });

    this.route.paramMap.subscribe(params => {
      this.corpPrefix = this.sessionContextService.getCorpPrefix();
      this.updateActiveItem();
    });

    this.routerSubscriber = this.router.events.pipe(filter(event => event instanceof NavigationEnd))
    .subscribe((event: any) => {
      let urlParts: string[] = event.url.split("/");
      if(urlParts.length > 0) {
        /*
          Get target screen - config in ROUTE_PATH
          Currently, this logic only working with screen that path at the end of url
        */
        const screenlink = urlParts[(urlParts.length - 1) < 0 ? 0 : urlParts.length - 1];
        const target = this.sidebarData.find(e => e.routerLink?.endsWith(screenlink));
        if(target) {
          this.sidebarData = this.sidebarData.map(e => {
            e.active = ((e.routerLink == target.routerLink) && e.display !== false) ? true : false; // Update selected menu at sidebar
            return e;
          });
          this.menuIcon = target?.icon || ''; // Update sub-header icon
          this.title = target?.title || ''; // Update sub-header screen name
        }
      }
    });
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.updateActiveItem();
      }
    });
  }

  updateActiveItem() {
    const currentUrl = this.router.url.replace(/^\//, ''); 
    this.sidebarData.forEach(item => {
      //item.routerLink = item.routerLink?.replace(':corpPrefix', this.corpPrefix);
      item.active = item.routerLink === currentUrl;
      if(item.active) {
        this.menuIcon = item?.icon || '';
        this.title = item?.title || '';
      }
    });

    // let urlSplit = currentUrl?.split("/");
    // if(urlSplit.length > 0) {
    //   const item = this.sidebarData.find(e => e.routerLink?.endsWith(urlSplit[urlSplit.length - 1]));
    //   this.title = item?.title ?? COMMON_TEXT.CHECK_SHEET_TITLE;
    //   this.menuIcon = item?.icon ?? 'check-sheet';
    // }
  }
  
  getActiveMenu(menu: any) {
    this.router.navigateByUrl(menu.routerLink);
  }

  logout() {
    this.authService.logout();
  }

  toggleFullScreen() {
    this.isFullScreen = !this.isFullScreen;
    const elem = this.fullScreenElement.nativeElement;
    this.fullScreenService.toggleFullscreen(elem);
  }

  handleToggleSidebar(value: any) {
    this.sidebarExpanded = value;
    this.toggleSidebar.emit(value);
  }
}
